import { Component, Inject, LOCALE_ID } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { FileService } from '../../shared/services/file.service';
import { WorkOrderService } from '../services/work.order.service';
import { DealerDocument } from '../../shared/models/dealer.document.interface';
import { QuoteService } from '../../quote/services/quote.service';
import { forkJoin } from 'rxjs';
import { NgForm } from '@angular/forms';

export interface WorkOrderPrintOptionsDialogData {
  workOrderId: number
}

export interface WorkOrderPrintMap {
  id: number
}

@Component({
  selector: 'app-work-order-print-dialog',
  templateUrl: './work-order-print-dialog.component.html',
  styleUrls: ['./work-order-print-dialog.component.scss']
})
export class WorkOrderPrintDialog extends AbstractBiditComponent {

  showPricing: boolean = false;
  locale: string = 'en';

  showPrintAttachments = false;
  showDocuments: boolean = false;
  documentsLoaded: boolean = false;
  dealerDocuments: DealerDocument[] = [];
  selectedDocuments: DealerDocument[] = [];
  documentBlobs: Blob[] = [];
  showDates: boolean = false;
   


  dateList: any[] = [];
  selectedStartDate: string = new Date().toLocaleDateString();
  selectedEndDate: string = new Date().toLocaleDateString();

  workOrderIds: number[] = [];
  workOrderIdMap: WorkOrderPrintMap[] = [];


  constructor(@Inject(LOCALE_ID) locale: string,
    deviceDetectorService: DeviceDetectorService,
    private fileService: FileService, private workOrderService: WorkOrderService,
    private quoteService: QuoteService,
    public dialogRef: MatDialogRef<WorkOrderPrintDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    super(deviceDetectorService);

    this.locale = locale;

    this.showDates = data.printingFromCalendar ?? false;

    if (this.showDates) {
      this.setupDates();
    }
  }

  printWorkOrder(): void {
    this.isRequesting = true;

    this.selectedDocuments.forEach(dd => {
      this.quoteService.getDocumentAttachment(dd.dealerDocument_ID)
        .subscribe({
          next: (result) => {
            this.documentBlobs.push(result);
          },
          error: (error: string) => this.onError(error)
        });
    })

    this.workOrderService.printWorkOrder(this.data.workOrderId, this.showPricing, this.locale)
      .subscribe(
        {
          next: (x) => {
            this.documentBlobs.unshift(x);
            this.isRequesting = false;
            this.fileService.downloadMergedFile(this.documentBlobs, 'WorkOrder.pdf');
            this.dialogRef.close();
          },
          error: (error: string) => this.onError(error)
        });
  }

  async printMultipleWorkOrders(form: NgForm): Promise<void> {
    if (!form.valid) {
      return;
    }

    if (this.workOrderIdMap.length <= 0) {
      alert(`There are no work orders to print for selected dates`)
      this.dialogRef.close();
      return;
    }

    console.log(`printing ${this.workOrderIdMap.length} workorders for ${this.selectedStartDate}`);
    this.isRequesting = true;    

    const observable = forkJoin(
      this.workOrderIdMap.map(({ id }: any) => this.workOrderService.printWorkOrder(id, this.showPricing, this.locale)));      

    observable.subscribe({
      next: value => this.documentBlobs = value,
      complete: () => {
        this.fileService.downloadMergedFile(this.documentBlobs, 'WorkOrder.pdf');
        console.log(`document = ${this.documentBlobs.length}`)
        console.log(`closing dialog`);
        this.dialogRef.close();

        this.isRequesting = false;
      }
    })   
  }

  documentSelectionUpdated(documentsSelected: DealerDocument[]) {
    this.selectedDocuments = documentsSelected;
  }

  loadDocuments(loadedDocuments: DealerDocument[]) {
    this.dealerDocuments = loadedDocuments;
    this.showPrintAttachments = this.data.showPrintAttachments && loadedDocuments.length > 0;
    this.documentsLoaded = true;
  }

  showDealerDocuments() {
    this.showDocuments = true;
  }

  dateChanged(form: NgForm) {
    if (!form.valid) {
      return;
    }

    this.getWorkOrdersForSelectedDate(this.selectedStartDate, this.selectedEndDate);
  }

  setupDates() {
    let weekStartDate = this.addDays(this.data.viewDate, (-1 * this.data.viewDate.getDay()));
    let nextDate = weekStartDate;
    this.dateList.push(weekStartDate.toLocaleDateString());
    this.selectedStartDate = weekStartDate.toLocaleDateString();

    for (let i = 0; i < 6; i++) {
      nextDate = this.addDays(nextDate, 1);
      this.dateList.push(nextDate.toLocaleDateString());
    }

    this.selectedEndDate = nextDate.toLocaleDateString();

    this.getWorkOrdersForSelectedDate(this.selectedStartDate, this.selectedEndDate);
  }

  getWorkOrdersForSelectedDate(selectedStartDate: string, selectedEndDate: string) {

    const startDateWorkOrders = [... new Set(this.data.workOrders.filter(wo => new Date(wo.workOrder_Start) >= new Date(selectedStartDate) &&  new Date(wo.workOrder_Start) <= new Date(selectedEndDate)).map(wo => wo.workOrder_ID))];
    const stopDateWorkOrders = [... new Set(this.data.workOrders.filter(wo => new Date(wo.workOrder_Stop) >= new Date(selectedStartDate) && new Date(wo.workOrder_Stop) <= new Date(selectedEndDate)).map(wo => wo.workOrder_ID))];
    //const startDateWorkOrders = [... new Set(this.data.workOrders.filter(wo => new Date(wo.workOrder_Start).toLocaleDateString() >= selectedStartDate &&  new Date(wo.workOrder_Start).toLocaleDateString() <= selectedEndDate).map(wo => wo.workOrder_ID))];
    //const stopDateWorkOrders = [... new Set(this.data.workOrders.filter(wo => new Date(wo.workOrder_Stop).toLocaleDateString() >= selectedStartDate && new Date(wo.workOrder_Stop).toLocaleDateString() <= selectedEndDate).map(wo => wo.workOrder_ID))];

    this.workOrderIds = [...new Set([...startDateWorkOrders, ...stopDateWorkOrders])] as number[];

    this.workOrderIdMap = [];
    for (let id of this.workOrderIds) {
      let idMap = { id: id };
      this.workOrderIdMap.push(idMap);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  addDays(date: Date, days: number): Date {
    let result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }
}
